import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "landscaping-web-design-colorado",
  metaTitle: "Landscaping Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom landscaping web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Landscaping marketing", to: "/landscaping-marketing-colorado" },
  ],
  currentLabel: "Landscaping Web Design Colorado",
  eyebrow: "Landscaping Web Design — Colorado",
  h1: (
    <>
      Landscaping <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded landscaping websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Landscaping websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first landscaping sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Landscaping web design in Colorado",
    body: "Landscaping web design is the practice of building conversion-focused, mobile-first websites for landscaping companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with image-heavy galleries that don't tank Core Web Vitals." },
    { title: "Seasonal service pages", body: "Spring cleanup, mowing, irrigation, hardscape, fall cleanup, snow removal — each its own ranking page." },
    { title: "Before/after galleries", body: "Lazy-loaded portfolio galleries optimized for mobile with schema for image SEO." },
    { title: "Quote request forms", body: "Property-size and project-type qualifying questions baked into the form to filter junk leads." },
    { title: "Service area maps", body: "Dedicated pages for every Colorado city you serve with proper LocalBusiness schema." },
    { title: "Schema and SEO baked in", body: "LocalBusiness, Service, FAQPage, Review schema at launch — no SEO retrofit." },
  ],
  whyThisMarket: [
    { heading: "Visual proof closes the lead", body: "Landscaping is a portfolio sale. We build galleries that load fast and surface your best work above the fold." },
    { heading: "Seasonal SEO requires seasonal pages", body: "Snow removal in November, spring cleanup in March — Google rewards landing pages built for each season." },
    { heading: "Most landscaping sites are slow", body: "Image-heavy templates routinely score under 30 on PageSpeed. Hand-coded sites with proper lazy loading hit 95+." },
  ],
  relatedLinks: [
    { to: "/landscaping-marketing-colorado", label: "Landscaping marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/landscaping-seo-colorado", label: "Landscaping SEO in Colorado" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado landscaping case studies" },
    { to: "/wix-vs-custom-website-colorado-landscaping", label: "Wix vs custom website for Landscaping" },
    { to: "/squarespace-vs-custom-website-colorado-landscaping", label: "Squarespace vs custom website for Landscaping" },
    { to: "/godaddy-vs-custom-website-colorado-landscaping", label: "GoDaddy vs custom website for Landscaping" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a landscaping website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a landscaping website?", a: "Typical timeline is 4–6 weeks for a 12–20 page landscaping site with full service and service-area pages. Larger multi-location landscaping companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs landscaping companies?", a: "No — we build landscaping sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Landscaping Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for landscaping companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/landscaping-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
